import { Alert, Button, Input } from "antd"
import React, { useState } from "react"
import styled from "styled-components"

import { postPasswordSignup } from "../../actions/passwordAuth"
import {
  PASSWORD_MIN_LENGTH,
  isValidEmail,
  passwordProblem,
  signupErrorKey,
} from "../../helpers/passwordAuth"
import { t } from "../../lib/i18n"

import ConfirmationResendForm from "./ConfirmationResendForm"
import { lightAntOverrides } from "./lightAntOverrides"

// サインアップしてもトークンは発行されない（auth-api はメール確認が済むまで
// ログインさせない）。成功したら確認メールの案内に切り替える。
const PasswordSignupForm = (): JSX.Element => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!isValidEmail(email)) {
      setErrorKey("invalid_email")
      return
    }
    const problem = passwordProblem(password)
    if (problem) {
      setErrorKey(problem)
      return
    }
    if (password !== passwordConfirmation) {
      setErrorKey("password_mismatch")
      return
    }

    setSubmitting(true)
    setErrorKey(null)
    const result = await postPasswordSignup({ email: email.trim(), password })
    setSubmitting(false)

    if (result.ok) {
      setSentTo(email.trim())
      return
    }
    setErrorKey(signupErrorKey(result))
  }

  if (sentTo) {
    return (
      <Root>
        <Alert
          type="success"
          message={t("signup_confirmation_sent", sentTo)}
          description={t("signup_confirmation_sent_description")}
        />
        <ConfirmationResendForm initialEmail={sentTo} />
      </Root>
    )
  }

  return (
    <Root>
      <StyledForm onSubmit={onSubmit} noValidate>
        <Input
          type="email"
          aria-label={t("email")}
          placeholder={t("email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <Input.Password
          aria-label={t("password")}
          placeholder={t("password_rule", String(PASSWORD_MIN_LENGTH))}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <Input.Password
          aria-label={t("password_confirmation")}
          placeholder={t("password_confirmation")}
          value={passwordConfirmation}
          onChange={(e) => setPasswordConfirmation(e.target.value)}
          autoComplete="new-password"
        />
        {errorKey && (
          <Alert
            type="error"
            message={t(errorKey, String(PASSWORD_MIN_LENGTH))}
          />
        )}
        <Button type="primary" htmlType="submit" loading={submitting} block>
          {t("password_signup_submit")}
        </Button>
      </StyledForm>
    </Root>
  )
}

const Root = styled.div`
  ${lightAntOverrides}

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px 0;
  width: 360px;
  max-width: 100%;
  margin: 0 auto;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px 0;
  width: 100%;
`

export default PasswordSignupForm
